import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/base/BackToTop';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getArticleBySlug, getArticlesByCategory, getCategoryBySlug } from '@/mocks/articles';

const articleImages: Record<string, string> = {
  'official-login-guide-2024': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20login%20page%20on%20a%20laptop%20screen%20with%20warm%20golden%20accents%2C%20clean%20professional%20financial%20platform%20interface%2C%20soft%20natural%20lighting%2C%20elegant%20workspace%20setting%2C%20warm%20beige%20background%2C%20editorial%20technology%20photography&width=1200&height=600&seq=article-detail-login-guide&orientation=landscape',
  'login-problem-solutions': 'https://readdy.ai/api/search-image?query=Customer%20support%20and%20help%20desk%20scene%20with%20warm%20ambient%20lighting%2C%20professional%20office%20environment%2C%20clean%20minimalist%20design%2C%20helpful%20atmosphere%2C%20warm%20beige%20tones&width=1200&height=600&seq=article-detail-login-problems&orientation=landscape',
  'platform-features-overview': 'https://readdy.ai/api/search-image?query=Modern%20financial%20trading%20platform%20dashboard%20on%20large%20monitor%20screen%2C%20clean%20minimalist%20interface%20showing%20market%20charts%2C%20professional%20trading%20environment%2C%20warm%20golden%20and%20charcoal%20color%20scheme&width=1200&height=600&seq=article-detail-platform&orientation=landscape',
  'market-trends-2025': 'https://readdy.ai/api/search-image?query=Professional%20financial%20market%20analysis%20scene%20with%20multiple%20screens%20showing%20stock%20charts%20and%20data%2C%20modern%20minimalist%20office%2C%20warm%20ambient%20lighting%2C%20clean%20professional%20aesthetic&width=1200&height=600&seq=article-detail-market&orientation=landscape',
  'account-security-guide': 'https://readdy.ai/api/search-image?query=Digital%20security%20and%20cybersecurity%20concept%20with%20shield%20icon%20on%20screen%2C%20warm%20lighting%2C%20modern%20tech%20office%2C%20professional%20atmosphere%2C%20clean%20minimalist%20design&width=1200&height=600&seq=article-detail-security&orientation=landscape',
  'fund-safety-system': 'https://readdy.ai/api/search-image?query=Financial%20security%20and%20safety%20concept%2C%20bank%20vault%20aesthetic%20in%20modern%20minimalist%20style%2C%20warm%20golden%20lighting%2C%20trust%20and%20protection%20atmosphere&width=1200&height=600&seq=article-detail-fund&orientation=landscape',
  'deposit-withdrawal-guide': 'https://readdy.ai/api/search-image?query=Digital%20payment%20and%20banking%20transaction%20concept%2C%20modern%20financial%20interface%20on%20screen%2C%20warm%20lighting%2C%20clean%20professional%20setting%2C%20minimalist%20design&width=1200&height=600&seq=article-detail-deposit&orientation=landscape',
  'new-user-registration-guide': 'https://readdy.ai/api/search-image?query=New%20user%20registration%20and%20onboarding%20concept%2C%20modern%20smartphone%20displaying%20signup%20form%2C%20warm%20lighting%2C%20clean%20minimalist%20environment&width=1200&height=600&seq=article-detail-registration&orientation=landscape',
};

const defaultImage = 'https://readdy.ai/api/search-image?query=Professional%20financial%20article%20detail%20header%20image%20with%20warm%20golden%20accents%2C%20clean%20minimalist%20design&width=1200&height=600&seq=article-detail-default&orientation=landscape';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-3">文章未找到</h2>
            <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const category = getCategoryBySlug(article.category);
  const relatedArticles = getArticlesByCategory(article.category)
    .filter((a) => a.slug !== article.slug)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: { '@type': 'Organization', name: article.author },
    datePublished: article.date,
    dateModified: article.date,
    publisher: {
      '@type': 'Organization',
      name: '众赢国际',
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - 众赢国际`}
        description={article.summary}
        keywords={`${article.title},众赢国际,${category?.name || ''}`}
        canonicalUrl={`https://ovelbrf.cn/article/${slug}`}
        ogType="article"
        articlePublishedTime={article.date}
        articleModifiedTime={article.date}
      />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <Navbar />
      <main className="pt-20 pb-16">
        <article className="w-full max-w-[860px] mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category?.name || '分类', href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>

          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              {category && (
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors cursor-pointer"
                >
                  {category.name}
                </Link>
              )}
              <span className="text-xs text-foreground-400">{article.date}</span>
              <span className="text-xs text-foreground-400">{article.readTime}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <p className="text-base text-foreground-500 leading-relaxed">{article.summary}</p>
          </div>

          <div className="mb-10 rounded-xl overflow-hidden aspect-[2/1]">
            <img
              src={articleImages[article.slug] || defaultImage}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose-custom text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <span>作者：{article.author}</span>
              <span>发布日期：{article.date}</span>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full max-w-[860px] mx-auto px-4 md:px-6 mt-16">
            <h3 className="font-heading text-xl font-bold text-foreground-950 mb-6">相关文章</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.slug}
                  to={`/article/${ra.slug}`}
                  className="group p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                >
                  <h4 className="font-heading text-sm font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {ra.title}
                  </h4>
                  <p className="text-xs text-foreground-500 line-clamp-2">{ra.summary}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}